import React, { useState, useEffect, Suspense } from "react";
import { AppErrorBoundary, lazyRetry } from "./components/AppErrorBoundary";
import { ConfirmHost } from "./components/ConfirmBox";
import "./lib/bookWait";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

/* New page → start at the top (so the show's Last Result card is the first thing seen, not a carried-over scroll offset) */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { I18nProvider } from "./i18n";
import SplashScreen from "./components/SplashScreen";
import AutoUpdate from "./components/AutoUpdate";
import { WelcomePopup } from "./components/WelcomePopup";
import { InstallGate } from "./components/InstallGate";
import { UpdateBanner } from "./components/UpdateBanner";
import { PushNudge } from "./components/PushNudge";
import { trackVisit } from "./lib/trackVisit";

import CustomerLayout from "./components/CustomerLayout";
import Home from "./pages/Home";
import Result from "./pages/Result";
import RoyalPlay from "./pages/RoyalPlay";
import GameDetail from "./pages/GameDetail";
import MyBets from "./pages/MyBets";
import Me from "./pages/Me";
import Auth from "./pages/Auth";
import Recharge from "./pages/Recharge";
import Withdrawal from "./pages/Withdrawal";
import CategoryGames from "./pages/CategoryGames";


import { initBrand } from "./lib/brand";

// Code-split: admin, agent and mini-game bundles load only when visited — keeps the customer app small & fast.
const AdminLayout = lazyRetry(() => import("./admin/AdminLayout"));
const Dashboard = lazyRetry(() => import("./admin/Dashboard"));
const AdminUsers = lazyRetry(() => import("./admin/AdminUsers"));
const AdminRecharges = lazyRetry(() => import("./admin/AdminRecharges"));
const AutoPayGuide = lazyRetry(() => import("./admin/AutoPayGuide"));
const AdminWithdrawals = lazyRetry(() => import("./admin/AdminWithdrawals"));
const AdminPendingWinners = lazyRetry(() => import("./admin/AdminPendingWinners"));
const AdminCashBook = lazyRetry(() => import("./admin/AdminCashBook"));
const AdminAutoResults = lazyRetry(() => import("./admin/AdminAutoResults"));
const AdminHandoffs = lazyRetry(() => import("./admin/AdminHandoffs"));
const AdminGames = lazyRetry(() => import("./admin/AdminGames"));
const AdminResults = lazyRetry(() => import("./admin/AdminResults"));
const AdminPayout = lazyRetry(() => import("./admin/AdminPayout"));
const AdminGiftCodes = lazyRetry(() => import("./admin/AdminGiftCodes"));
const AdminAudit = lazyRetry(() => import("./admin/AdminAudit"));
const WinnersBoard = lazyRetry(() => import("./components/WinnersBoard"));
const OtpDesk = lazyRetry(() => import("./components/OtpDesk"));
const AdminShareLinks = lazyRetry(() => import("./admin/AdminShareLinks"));
const AdminBanners = lazyRetry(() => import("./admin/AdminBanners"));
const AdminSettings = lazyRetry(() => import("./admin/AdminSettings"));
const AdminMiniGames = lazyRetry(() => import("./admin/AdminMiniGames"));
const AdminBookings = lazyRetry(() => import("./admin/AdminBookings"));
const AdminCompanyBill = lazyRetry(() => import("./admin/AdminCompanyBill"));
const AdminAgents = lazyRetry(() => import("./admin/AdminAgents"));
const AdminLeaveDesk = lazyRetry(() => import("./admin/AdminLeaveDesk"));
const AdminAgentDesk = lazyRetry(() => import("./admin/AdminAgentDesk"));
const AdminSuspicious = lazyRetry(() => import("./admin/AdminSuspicious"));
const AdminRemarketing = lazyRetry(() => import("./admin/AdminRemarketing"));
const AdminCompanySheets = lazyRetry(() => import("./admin/AdminCompanySheets"));
const AdminMarketing = lazyRetry(() => import("./admin/AdminMarketing"));
const AgentLayout = lazyRetry(() => import("./agent/AgentLayout"));
const AgentDashboard = lazyRetry(() => import("./agent/AgentDashboard"));
const AgentCustomers = lazyRetry(() => import("./agent/AgentCustomers"));
const AgentBookings = lazyRetry(() => import("./agent/AgentBookings"));
const Customer360 = lazyRetry(() => import("./components/customer360/Customer360"));
const MyAccount = lazyRetry(() => import("./pages/MyAccount"));
const AgentCommission = lazyRetry(() => import("./agent/AgentCommission"));
const AgentApprovals = lazyRetry(() => import("./agent/AgentApprovals"));
const AgentBook = lazyRetry(() => import("./agent/AgentBook"));
const MagicLogin = lazyRetry(() => import("./pages/AssistLogin").then((m) => ({ default: m.MagicLogin })));
const PinLogin = lazyRetry(() => import("./pages/AssistLogin").then((m) => ({ default: m.PinLogin })));
const AgentPromote = lazyRetry(() => import("./agent/AgentPromote"));
const AgentPassbook = lazyRetry(() => import("./agent/AgentPassbook"));
const AgentCompanyBill = lazyRetry(() => import("./agent/AgentCompanyBill"));
const AgentResults = lazyRetry(() => import("./agent/AgentResults"));
const AgentGames = lazyRetry(() => import("./agent/AgentGames"));
const AdminPayments = lazyRetry(() => import("./admin/AdminPayments"));
const DiceGame = lazyRetry(() => import("./pages/DiceGame"));
const AndarBahar = lazyRetry(() => import("./pages/AndarBahar"));
const MoneySlot = lazyRetry(() => import("./pages/MoneySlot"));
const SevenLive = lazyRetry(() => import("./pages/SevenLive"));
const PickGame = lazyRetry(() => import("./pages/PickGame"));
const RoundGame = lazyRetry(() => import("./pages/RoundGame"));
const Leaderboard = lazyRetry(() => import("./pages/Leaderboard"));
const Referral = lazyRetry(() => import("./pages/Referral"));
const GiftCode = lazyRetry(() => import("./pages/GiftCode"));
const Transactions = lazyRetry(() => import("./pages/Transactions"));
const RateCard = lazyRetry(() => import("./pages/RateCard"));
const RateCardPrint = lazyRetry(() => import("./pages/RateCardPrint"));
const Poster = lazyRetry(() => import("./pages/Poster"));
const AppDownload = lazyRetry(() => import("./pages/AppDownload"));
const PosterBulk = lazyRetry(() => import("./pages/PosterBulk"));

// Marketing: remember ?src=instagram|whatsapp|youtube (and ?code=) from the landing link until the user signs up.
(() => { try { const qs = new URLSearchParams(window.location.search); const src = qs.get("src") || qs.get("utm_source"); if (src) localStorage.setItem("rl_src", src.toLowerCase().slice(0, 30)); } catch {} })();
trackVisit();

function App() {
  useEffect(() => { initBrand(); }, []);
  const [showSplash, setShowSplash] = useState(() => (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true) && !sessionStorage.getItem("rl_splash_done") && !/^\/(admin|agent)/.test(window.location.pathname));
  return (
    <AppErrorBoundary>
    <I18nProvider>
      <AutoUpdate />
      <ConfirmHost />
      {showSplash && <SplashScreen onDone={() => { sessionStorage.setItem("rl_splash_done", "1"); setShowSplash(false); }} />}
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400 text-sm" data-testid="route-loading">…</div>}>
            <Routes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/staff" element={<Auth />} />
              <Route path="/rate-card/print" element={<RateCardPrint />} />
              <Route path="/poster" element={<Poster />} />
              <Route path="/app" element={<AppDownload />} />
              <Route path="/poster/bulk" element={<PosterBulk />} />
              <Route path="/login" element={<Navigate to="/auth" replace />} />
              <Route path="/m/:token" element={<MagicLogin />} />
              <Route path="/pin" element={<PinLogin />} />

              <Route element={<CustomerLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/simple" element={<Navigate to="/" replace />} />
                <Route path="/simple/recharge" element={<Navigate to="/recharge" replace />} />
                <Route path="/result" element={<Result />} />
                <Route path="/play" element={<RoyalPlay />} />
                <Route path="/play/:gameId" element={<GameDetail />} />
                <Route path="/my-bets" element={<MyBets />} />
                <Route path="/my-account" element={<MyAccount />} />
                <Route path="/rate-card" element={<RateCard />} />
                <Route path="/me" element={<Me />} />
                <Route path="/recharge" element={<Recharge />} />
                <Route path="/withdraw" element={<Withdrawal />} />
                <Route path="/referral" element={<Referral />} />
                <Route path="/gift-code" element={<GiftCode />} />
                <Route path="/transactions" element={<Transactions />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/category/:name" element={<CategoryGames />} />
                <Route path="/mini/dice" element={<DiceGame />} />
                <Route path="/mini/andar-bahar" element={<AndarBahar />} />
                <Route path="/mini/slot/money" element={<MoneySlot />} />
                <Route path="/mini/live/seven" element={<SevenLive />} />
                <Route path="/mini/pick/:game" element={<PickGame />} />
                <Route path="/mini/round/:game" element={<RoundGame />} />
              </Route>

              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="customer/:id" element={<Customer360 />} />
                <Route path="recharges" element={<AdminRecharges />} />
                <Route path="payments" element={<AdminPayments />} />
                <Route path="autopay-guide" element={<AutoPayGuide />} />
                <Route path="withdrawals" element={<AdminWithdrawals />} />
                <Route path="pending-winners" element={<AdminPendingWinners />} />
                <Route path="cash-book" element={<AdminCashBook />} />
                <Route path="auto-results" element={<AdminAutoResults />} />
                <Route path="handoffs" element={<AdminHandoffs />} />
                <Route path="games" element={<AdminGames />} />
                <Route path="bookings" element={<AdminBookings />} />
                <Route path="company-bill" element={<AdminCompanyBill />} />
                <Route path="agents" element={<AdminAgents />} />
                <Route path="leave-desk" element={<AdminLeaveDesk />} />
                <Route path="agent-desk" element={<AdminAgentDesk />} />
                <Route path="risk" element={<AdminSuspicious />} />
                <Route path="company-sheets" element={<AdminCompanySheets />} />
                <Route path="marketing" element={<AdminMarketing />} />
                <Route path="remarketing" element={<AdminRemarketing />} />
                <Route path="results" element={<AdminResults />} />
                <Route path="payout" element={<AdminPayout />} />
                <Route path="gift-codes" element={<AdminGiftCodes />} />
                <Route path="audit" element={<AdminAudit />} />
                <Route path="winners" element={<WinnersBoard />} />
                <Route path="otp-desk" element={<OtpDesk />} />
                <Route path="share-links" element={<AdminShareLinks />} />
                <Route path="banners" element={<AdminBanners />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="mini-games" element={<AdminMiniGames />} />
              </Route>

              <Route path="/agent" element={<AgentLayout />}>
                <Route index element={<AgentDashboard />} />
                <Route path="customers" element={<AgentCustomers />} />
                <Route path="customer/:id" element={<Customer360 />} />
                <Route path="bookings" element={<AgentBookings />} />
                <Route path="commission" element={<AgentCommission />} />
                <Route path="approvals" element={<AgentApprovals />} />
                <Route path="winners" element={<WinnersBoard />} />
                <Route path="otp-desk" element={<OtpDesk />} />
                <Route path="results" element={<AgentResults />} />
                <Route path="games" element={<AgentGames />} />
                <Route path="passbook" element={<AgentPassbook />} />
                <Route path="bill" element={<AgentCompanyBill />} />
                <Route path="book" element={<AgentBook />} />
                <Route path="promote" element={<AgentPromote />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
            <Toaster position="top-center" richColors theme="light" />
            <WelcomePopup />
            <InstallGate />
            <UpdateBanner />
            <PushNudge />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </I18nProvider>
    </AppErrorBoundary>
  );
}

export default App;
